import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, X, Phone, Mail, Briefcase, Calendar, Download, RefreshCw, Handshake } from "lucide-react";
import { adminClient } from "@/lib/apiClient";
import { PARTNER_APP_STATUSES } from "@/data/projectListingTemplates";

const STATUS_TONE = {
  slate: "bg-slate-200 text-slate-700",
  amber: "bg-amber-100 text-amber-700",
  red: "bg-red-100 text-red-700",
  violet: "bg-violet-100 text-violet-700",
  sky: "bg-sky-100 text-sky-700",
  emerald: "bg-emerald-100 text-emerald-700",
  gray: "bg-gray-200 text-gray-600",
};

function StatusPill({ status }) {
  const meta = PARTNER_APP_STATUSES.find((s) => s.slug === status) || PARTNER_APP_STATUSES[0];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${STATUS_TONE[meta.tone] || STATUS_TONE.gray}`}>
      {meta.label}
    </span>
  );
}

function ApplicationDrawer({ app, onClose, onStatusChange }) {
  if (!app) return null;
  const fields = [
    ["Trade / Specialty", app.trade_specialty],
    ["Service Areas", app.service_areas],
    ["Crew Size", app.crew_size],
    ["Years in Business", app.years_in_business],
    ["Insurance Status", app.insurance_status],
    ["License Status", app.license_status],
    ["Availability", app.availability],
    ["Website / Social", app.website_or_social],
  ].filter(([, v]) => v && String(v).trim());

  return (
    <div className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-stretch justify-end" role="dialog" aria-modal="true" data-testid="partner-app-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-br-paper">
          <div>
            <div className="br-eyebrow text-br-bronze">Trade Partner Application</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">{app.company_name}</h3>
            <div className="text-xs text-gray-600 mt-0.5">{app.contact_name}{app.project_title ? ` — ${app.project_title}` : ""}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="partner-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700"><Phone size={14} className="text-br-bronze" /> {app.phone}</div>
            <div className="flex items-center gap-2 text-gray-700"><Mail size={14} className="text-br-bronze" /> {app.email}</div>
            <div className="flex items-center gap-2 text-gray-700"><Calendar size={14} className="text-br-bronze" /> {new Date(app.created_at).toLocaleString()}</div>
          </div>

          <div className="bg-br-paper border border-gray-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Status</div>
            <div className="flex flex-wrap gap-1.5" data-testid="partner-status-row">
              {PARTNER_APP_STATUSES.map((s) => {
                const isActive = app.status === s.slug;
                return (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => onStatusChange(app, s.slug)}
                    className={`px-3 py-1 rounded text-[10px] uppercase tracking-widest font-semibold border transition ${isActive ? "bg-br-bronze border-br-bronze text-white" : "bg-white border-gray-200 text-gray-700 hover:border-br-bronze"}`}
                    data-testid={`partner-status-${s.slug}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {fields.map(([k, v]) => (
              <div key={k} className="bg-br-paper/40 border border-gray-100 rounded px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">{k}</div>
                <div className="text-sm text-gray-900 mt-0.5">{v}</div>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-2">
            {[
              ["Site walk", app.can_attend_site_walk],
              ["Labor only", app.can_provide_labor_only],
              ["Labor + materials", app.can_provide_labor_and_materials],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm border border-gray-100 rounded px-3 py-2 bg-br-paper/40">
                <span className="text-gray-700">{k}</span>
                <span className="font-semibold text-gray-900">{v === true ? "Yes" : v === false ? "No" : "—"}</span>
              </div>
            ))}
          </div>

          {app.message && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Message</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{app.message}</p>
            </div>
          )}
          {app.references && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">References</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{app.references}</p>
            </div>
          )}

          {Array.isArray(app.files) && app.files.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Documents</div>
              <ul className="space-y-2">
                {app.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-br-paper border border-gray-200 rounded px-3 py-2">
                    <Briefcase size={14} className="text-br-bronze" />
                    <span className="truncate flex-1">{f.filename || `file-${i + 1}`}</span>
                    <a href={f.data_url} download={f.filename || `file-${i + 1}`} className="text-br-bronze hover:text-br-bronze-2 text-[11px] uppercase tracking-widest font-bold" data-testid={`partner-file-${i}`}>
                      <Download size={12} className="inline" /> Download
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPartnerApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      const res = await adminClient.get("/admin/project-partner-applications", { params });
      setApps(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load partner applications.");
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { load(); }, [load]);

  const filteredApps = useMemo(() => {
    if (!filters.search) return apps;
    const q = filters.search.toLowerCase();
    return apps.filter((a) =>
      (a.company_name && a.company_name.toLowerCase().includes(q)) ||
      (a.contact_name && a.contact_name.toLowerCase().includes(q)) ||
      (a.email && a.email.toLowerCase().includes(q)) ||
      (a.trade_specialty && a.trade_specialty.toLowerCase().includes(q))
    );
  }, [apps, filters.search]);

  const openApp = async (app) => {
    try {
      const res = await adminClient.get(`/admin/project-partner-applications/${app.id}`);
      setSelected(res.data);
    } catch (err) {
      setSelected(app);
    }
  };

  const updateStatus = async (app, status) => {
    try {
      await adminClient.patch(`/admin/project-partner-applications/${app.id}`, { status });
      setSelected((s) => (s && s.id === app.id ? { ...s, status } : s));
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Could not update status.");
    }
  };

  return (
    <div data-testid="admin-partner-apps">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="br-eyebrow">Partner Pipeline</div>
          <h2 className="font-display text-2xl tracking-widest uppercase mt-1">Project Partner Applications</h2>
          <p className="text-sm text-gray-600 mt-1">Trade partners who applied for open project scopes via /project-listings.</p>
        </div>
        <button type="button" onClick={load} className="br-btn-dark text-[11px]" data-testid="partner-apps-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="partner-apps-filters">
        <div>
          <label className="label-br">Search</label>
          <input type="text" className="input-br" placeholder="Company, contact, email, trade" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="partner-apps-filter-search" />
        </div>
        <div>
          <label className="label-br">Status</label>
          <select className="select-br" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="partner-apps-filter-status">
            <option value="">All</option>
            {PARTNER_APP_STATUSES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="partner-apps-error">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="partner-apps-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading partner applications…
        </div>
      ) : filteredApps.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500" data-testid="partner-apps-empty">No partner applications match your filters.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-br-paper border-b border-gray-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-600">
                  <th className="px-4 py-3 font-bold">Company</th>
                  <th className="px-4 py-3 font-bold">Project</th>
                  <th className="px-4 py-3 font-bold">Trade</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map((a) => (
                  <tr key={a.id} className="hover:bg-br-paper/30 cursor-pointer" onClick={() => openApp(a)} data-testid={`partner-app-row-${a.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{a.company_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{a.contact_name} · {a.email}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{a.project_title || "—"}</td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{a.trade_specialty || "—"}</td>
                    <td className="px-4 py-3 align-top"><StatusPill status={a.status} /></td>
                    <td className="px-4 py-3 align-top text-[11px] text-gray-500">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ApplicationDrawer app={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} />
    </div>
  );
}
